import React, { memo, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";

import ActionEnum from "enum/action";

import api from "services/api";

import pathnames from "routes/pathnames";

import ErrorResponseDto from "dto/services/error-response-dto";
import ClientsTableProps from "dto/components/pages/clients/page-clients-table-dto";

import formatLastUpdate from "common/format-last-update";
import formatIndexNumber from "common/format-index-number";
import serveRequestErrors from "common/serve-request-errors";
import serveRequestSuccess from "common/serve-request-success";

import CONSTANTS from "constants/constant";
import { statusOptions } from "constants/status-options";

import AppPagination from "components/app-pagination";

import arrowUp from "assets/images/components/app-faq-table/arrow-up.svg";
import arrowDown from "assets/images/components/app-faq-table/arrow-down.svg";
import editIcon from "assets/images/components/app-articles-table/edit-icon.svg";

const ClientsTable: React.FC<ClientsTableProps> = ({ clientData, fetching, onPageChange, paginationTable }) => {
	const navigate = useNavigate();
	const startIndex = paginationTable.currentPage * paginationTable.tableSize;
	const isEmptyData = useMemo(() => clientData.length === 0 && !fetching, [clientData.length, fetching]);

	//prettier-ignore
	const onHandleEdit = useCallback((id: string) => {
        navigate(pathnames.client + id);
    }, [navigate]);

	const getLabel = (options: { label: string; value: string }[], value: string) => {
		return options.find((option) => option.value === value)?.label || value;
	};

	//prettier-ignore
	const updateSortNumber = useCallback(async (id: string, newSortNumber: number) => {
		try {
		  	const response = await api.put.client.update_sortNumber({id}, newSortNumber);
			  
			if (response) serveRequestSuccess(CONSTANTS.MESSAGES.UPDATED, ActionEnum.Update);
		} catch (error) {
			const err = error as Error | ErrorResponseDto;

			serveRequestErrors(err);
		}
	}, []);

	//prettier-ignore
	const updateClientData = useCallback(async (id: string, newSortNumber: number, targetPage: number) => {
		await updateSortNumber(id, newSortNumber);
		onPageChange(targetPage);
	}, [updateSortNumber, onPageChange]);

	//prettier-ignore
	const handlePageMovement = useCallback(async (id: string, targetPage: number, isNextPage: boolean, paginationTable: { currentPage: number; totalPages: number; tableSize: number }) => {
		try {
			const payload = { size: paginationTable.tableSize, page: targetPage };
			const response = await api.get.client.clients(payload);
			const pageData = response?.data?.data?.list?.content || [];

			if (pageData.length > 0) {
				const targetItem = isNextPage ? pageData[0] : pageData[pageData.length - 1];
				await updateClientData(id, targetItem.sortNumber, targetPage);
			}
		} catch (error) {
			const err = error as Error | ErrorResponseDto;
			serveRequestErrors(err);
		}
	}, [updateClientData] );

	//prettier-ignore
	const onHandleMove = useCallback(async (id: string, move: number) => {
		const index = clientData.findIndex((client) => client.id === id);

		const updatedClient = clientData[index];

		const isFirstItem = index === 0 && move === -1;
		
		const isLastItem = index === clientData.length - 1 && move === 1;
		
		const moveToPrevPage = isFirstItem && paginationTable.currentPage > 0;
		
		const moveToNextPage = isLastItem && paginationTable.currentPage < paginationTable.totalPages - 1;

		if (!isFirstItem && !isLastItem) {
			const newClients = [...clientData];
			
			newClients.splice(index, 1);

			const movedDataSortNumber = clientData[index + move].sortNumber;

			newClients.splice(index + move, 0, { ...updatedClient, sortNumber: movedDataSortNumber });
			await updateClientData(id, movedDataSortNumber, paginationTable.currentPage);
		} else if (moveToPrevPage) {
			await handlePageMovement(id, paginationTable.currentPage - 1, false, paginationTable);
		} else if (moveToNextPage) {
			await handlePageMovement(id, paginationTable.currentPage + 1, true, paginationTable);
		}
	}, [clientData, paginationTable, updateClientData, handlePageMovement]);

	return (
		<div className="app-clients-table">
			<div className="clients-table">
				<div className="clients-table__wrapper">
					<table className="clients-table__container">
						<thead className="clients-table__header">
							<tr>
								<th className="clients-table__th">No.</th>

								<th className="clients-table__th">Client Name</th>

								<th className="clients-table__th">Type</th>

								<th className="clients-table__th">Publication Date</th>

								<th className="clients-table__th">Status</th>

								<th className="clients-table__th">Last Update</th>
							</tr>
						</thead>

						<tbody className="clients-table__content">
							{!isEmptyData &&
								clientData.map((data, index) => (
									<tr className="clients-table__tr" key={data.id}>
										<td className="clients-table__td">{formatIndexNumber(index + 1 + startIndex)}</td>

										<td className="clients-table__td">{data.title}</td>

										<td className="clients-table__td">{data.projectType}</td>

										<td className="clients-table__td">{moment(data.publicationDate).format(CONSTANTS.LISTING_DATE_FORMAT)}</td>

										<td className={data.status === "active" ? "clients-table__td--active" : "clients-table__td--inactive"}>{getLabel(statusOptions, data.status)}</td>

										<td className="clients-table__td">{formatLastUpdate(data.updatedAt)}</td>

										<td className="clients-table__td">
											<div className="clients-table__actions">
												<div className="clients-table__edit">
													<button className="clients-table__icon-button--bordered" onClick={() => onHandleMove(data.id, -1)}>
														<img src={arrowUp} alt="up" />
													</button>

													<button className="clients-table__icon-button--bordered" onClick={() => onHandleMove(data.id, 1)}>
														<img src={arrowDown} alt="down" />
													</button>
												</div>

												<button className="clients-table__edit-button" onClick={() => onHandleEdit(data.id)}>
													<img src={editIcon} alt="Edit" />
												</button>
											</div>
										</td>
									</tr>
								))}

							{fetching && (
								<tr className="clients-table__tr">
									<td className="clients-table__message" colSpan={7}>
										Loading...
									</td>
								</tr>
							)}

							{isEmptyData && (
								<tr className="clients-table__tr">
									<td className="clients-table__message" colSpan={7}>
										No records found
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>

				{!isEmptyData && !fetching && <AppPagination currentPage={paginationTable.currentPage} totalPages={paginationTable.totalPages} onPageChange={onPageChange} numberOfElements={paginationTable.numberOfElements} />}
			</div>
		</div>
	);
};

export default memo(ClientsTable);
